import HttpClient from "../http-client";
import { devLog } from "../common";

// TL → TD logo handover (Addendum 2 §5 / §6).
//
// §5.2 — TL Links stay supported through shadow TL: when the customer arrives via a webshop URL carrying
// the `tlgd` parameter (a pre-branded TronLogo visualization link), the logo guid is remembered for the
// session so it can be imported into the TD editor when the customer moves from visualization into the
// design flow — the selected logo must not get lost on the way.
//
// §6.1 — before any TL logo is auto-imported into the editor, it must be confirmed as connected to the
// current user; a logo the TL engine merely remembers from another webshop/user context must never be
// imported silently. Confirmation runs server-side (no CORS constraints): TronShop probes the user-scoped
// TL download URL — the same URL TD would fetch the logo from — so a positive verdict means both
// "belongs to this TL user" and "actually importable" (a negative one also protects the editor from
// aborting on a dead logo URL).

const TL_LINK_LOGO_KEY = "td_tl_link_logo_guid";
const VERDICT_CACHE_KEY = "td_tl_logo_import_verdicts";

// §5.2 step 2 — remember the TL-link logo for the session. Called wherever the tlgd parameter is
// processed (TronLogoLinks feeds it to the TL engine for the visualization at the same moment).
export function rememberTlLinkLogo(logoGuid: string): void {
    if (!logoGuid) {
        return;
    }
    try {
        window.sessionStorage.setItem(TL_LINK_LOGO_KEY, logoGuid);
    } catch {
        // sessionStorage unavailable — the TL engine's own memory still covers the visualization;
        // only the session-pinned TD import preference is lost.
    }
}

export function getTlLinkLogoGuid(): string | null {
    try {
        return window.sessionStorage.getItem(TL_LINK_LOGO_KEY);
    } catch {
        return null;
    }
}

type VerdictCache = Record<string, boolean>; // "{userGuid}:{logoGuid}" -> importable

function readVerdicts(): VerdictCache {
    try {
        const raw = window.sessionStorage.getItem(VERDICT_CACHE_KEY);
        return raw ? JSON.parse(raw) as VerdictCache : {};
    } catch {
        return {};
    }
}

function writeVerdicts(cache: VerdictCache): void {
    try {
        window.sessionStorage.setItem(VERDICT_CACHE_KEY, JSON.stringify(cache));
    } catch {
        // Cache is an optimization only — without it every editor open re-probes.
    }
}

// §6.1 — asks the server whether the logo resolves under the current TL user's download URL. Verdicts are
// session-cached: a logo does not change owners, and the probe result only needs to be fresh per session.
export async function isTlLogoImportable(userGuid: string, logoGuid: string): Promise<boolean> {
    const cacheKey = `${userGuid}:${logoGuid}`;
    const cache = readVerdicts();
    if (cacheKey in cache) {
        return cache[cacheKey];
    }

    let importable = false;
    // Only a genuine server answer is a stable verdict worth caching. A transient network failure /
    // non-ok response must NOT be cached — otherwise one blip would poison the session and permanently
    // disable auto-import for this logo even after the network recovers. Those cases fail closed for
    // THIS call but re-probe next time.
    let hasDefinitiveVerdict = false;
    try {
        // The empty onError suppresses HttpClient's generic error toast — a failed probe is not a
        // user-facing error, it just means the logo is not auto-imported (§6.1 fail-closed).
        const response = await new HttpClient().fetch(
            `/api/tronlogo/importable/${encodeURIComponent(userGuid)}/${encodeURIComponent(logoGuid)}`,
            undefined,
            () => { /* fail closed */ }
        );
        if (response?.ok) {
            importable = (await response.json() as { importable: boolean }).importable === true;
            hasDefinitiveVerdict = true;
        }
    } catch (err) {
        // Unconfirmed means not imported (§6.1) — fail closed for this call, but don't cache it.
        devLog("TronLogo import probe failed", err);
    }

    if (hasDefinitiveVerdict) {
        cache[cacheKey] = importable;
        writeVerdicts(cache);
    }
    return importable;
}
